import { useTranslation } from 'react-i18next';
import { Icon } from './Icon';
import { MAX_FAVOURITE_TEMPLATES, useFavouriteTemplates, type FavouriteRef } from '../hooks/useFavouriteTemplates';

interface FavouriteHeartProps {
  readonly favourite: FavouriteRef;
  /** The structure's display name, for the button's accessible label. */
  readonly name: string;
}

// #415 -- the heart that sits in a structure thumbnail's top-right corner. Empty heart = not on
// the Templates shelf; filled = on it. Disabled (with a "shelf full" title) once the shelf holds
// MAX_FAVOURITE_TEMPLATES and this structure isn't one of them. The caller positions it by
// wrapping the thumbnail in a `position: relative` box.
export function FavouriteHeart({ favourite, name }: FavouriteHeartProps) {
  const { t } = useTranslation();
  const { isFavourite, canFavourite, toggle } = useFavouriteTemplates();

  const saved = isFavourite(favourite);
  const blocked = !saved && !canFavourite(favourite);
  const label = saved
    ? t('workspace.templates.favourite.remove', { name })
    : blocked
      ? t('workspace.templates.favourite.full', { max: MAX_FAVOURITE_TEMPLATES })
      : t('workspace.templates.favourite.add', { name });

  return (
    <button
      type="button"
      className={`favourite-heart ${saved ? 'active' : ''}`}
      aria-pressed={saved}
      disabled={blocked}
      aria-label={label}
      title={label}
      onClick={() => toggle(favourite)}
    >
      <Icon name={saved ? 'favorite' : 'favoriteOutline'} size="1.05rem" />
    </button>
  );
}
